import type { PageExperience } from '@/features/experiences/types';
import { DEFAULT_BLOCK_THEME_ID } from '@/features/printables/theme';
import {
  ExperienceLayouts,
  ExperiencePrintableLayouts,
  PrintableBackground,
  type PrintableConfig,
  type PrintableConfigInput,
  type UpdateExperienceInput,
} from '@/types/gql.generated';

export type EditableFields = Pick<
  UpdateExperienceInput,
  | 'title'
  | 'description'
  | 'layoutId'
  | 'printableConfig'
  | 'styleId'
  | 'eventListStartDate'
  | 'eventListEndDate'
  | 'eventListDisplay'
>;

export const DEFAULT_PRINTABLE_CONFIG: PrintableConfig = {
  background: PrintableBackground.Cloud_1,
  isEmojiEnabled: false,
  isEndTimeEnabled: false,
  isYearEnabled: false,
  isLocationEnabled: false,
  isStartTimeEnabled: false,
} as const;

export const toPrintableConfig = (
  experience: PageExperience
): PrintableConfig => {
  return experience.printableConfig ?? { ...DEFAULT_PRINTABLE_CONFIG };
};

/**
 * The printable display toggles now live on first-class experience columns
 * rather than the printableConfig JSON blob. This maps the columns back to the
 * legacy `is*Enabled` names the templates read, so consumers stay unchanged as
 * the JSON keys are retired. Writes still go through `printableConfig`; the
 * server mirrors them onto these columns.
 */
export const getPrintableToggles = (experience: PageExperience) => ({
  isEmojiEnabled: experience.eventCardEmojiEnabled,
  isEndTimeEnabled: experience.eventCardEndTimeEnabled,
  isStartTimeEnabled: experience.eventCardStartTimeEnabled,
  isLocationEnabled: experience.eventCardLocationEnabled,
  isYearEnabled: experience.eventCardYearEnabled,
});

export const isPrintableLayout = (
  layoutId: string
): layoutId is ExperiencePrintableLayouts => {
  const printableValues: string[] = Object.values(ExperiencePrintableLayouts);

  return printableValues.includes(layoutId);
};

export const toPrintableLayout = (
  layout: ExperienceLayouts
): ExperiencePrintableLayouts => {
  if (isPrintableLayout(layout)) {
    return layout;
  }

  return ExperiencePrintableLayouts.PrintableListVertical;
};

const INTERACTIVE_TEMPLATE_LAYOUTS = [ExperienceLayouts.DayByCategory] as const;

export type TemplateLayout =
  ExperiencePrintableLayouts | (typeof INTERACTIVE_TEMPLATE_LAYOUTS)[number];

export const isTemplateLayout = (
  layoutId: string
): layoutId is TemplateLayout =>
  isPrintableLayout(layoutId) ||
  (INTERACTIVE_TEMPLATE_LAYOUTS as readonly string[]).includes(layoutId);

export const toTemplateLayout = (layout: ExperienceLayouts): TemplateLayout =>
  isTemplateLayout(layout)
    ? layout
    : ExperiencePrintableLayouts.PrintableListVertical;

const toPrintableInput = (config: PrintableConfig): PrintableConfigInput => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { backgroundUrl: _backgroundUrl, ...rest } = config;
  return rest;
};

export const experienceToUpdates = (
  experience: PageExperience | EditableFields
): EditableFields => {
  return {
    title: experience.title,
    description: experience.description ?? undefined,
    printableConfig: experience.printableConfig
      ? toPrintableInput(experience.printableConfig)
      : { ...DEFAULT_PRINTABLE_CONFIG },
    layoutId: experience.layoutId,
    styleId: experience.styleId || DEFAULT_BLOCK_THEME_ID,
    eventListStartDate: experience.eventListStartDate,
    eventListEndDate: experience.eventListEndDate,
    eventListDisplay: experience.eventListDisplay,
  };
};
